import { useCallback } from 'react';

import { UseDeleteUserProps, UseDeleteUserReturnType } from './useDeleteUser.decl';

import { DeleteAgentMutation, DeleteAgentMutationVariables } from '@generated/DeleteAgentMutation';
import { DELETE_AGENT_MUTATION } from '@graphql/mutations/DeleteAgentMutation';
import { SEARCH_TOTAL_QUERY } from '@graphql/queries/SearchTotalQuery';
import { getExternalErrorTranslationStrings } from '@helpers/errors.helpers';
import { useGraphMutation } from '@hooks/useMutation';
import { useTracker } from '@hooks/useTracker/useTracker';
import noop from 'lodash-es/noop';

/**
 * Hook to delete a user given a user id.
 * @param useDeleteUserProps - destructured parameter
 * @returns an object holding a function responsible for deleting a user, and its states
 */
export function useDeleteUser({
  userId,
  onCompleted,
}: UseDeleteUserProps): UseDeleteUserReturnType {
  const { track } = useTracker();

  const [deleteUser, { loading, error }] = useGraphMutation<
    DeleteAgentMutation,
    DeleteAgentMutationVariables
  >(DELETE_AGENT_MUTATION, {
    onError: noop,
    onCompleted: () => {
      onCompleted?.();
      track({ event: 'user_deleted' });
    },
  });

  const onSubmit = useCallback(() => {
    const ID = userId.toString();

    return deleteUser({
      variables: {
        input: {
          ID,
        },
      },
      update: (cache) => {
        cache.modify({
          id: cache.identify({ __typename: 'AgentV2', ID }),
          fields: (_, { DELETE }) => DELETE,
        });
      },
      optimisticResponse: {
        deleteAgent: {
          __typename: 'DeleteAgentOutput',
          ID,
        },
      },
      // refetch total query after deletion
      refetchQueries: [{ query: SEARCH_TOTAL_QUERY }],
    });
  }, [deleteUser, userId]);

  return {
    isDeleteLoading: loading,
    deleteError: error ? getExternalErrorTranslationStrings(error) : undefined,
    onSubmit,
  };
}
